// src/components/pages/InstructionDetailPage/InstructionDetailPage.tsx
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import {BackIcon} from '../../assets/images/BackIcon';
import {platformIcons} from '../../assets/images/platformIcons';
import {WireGuardIcon, AmneziaWgIcon, VlessIcon, IncyIcon} from '../../assets/images/ProtocolIcons';
import {createPortal} from 'react-dom';
import Loader from '../../widgets/Loader/Loader';
import {imageInstructionsData, instructionsData} from './instructionData'

import './InstructionDetailPage.scss';

interface InstructionData {
    platform: string;
    protocol: string;
    icon: string;
    installDescription: string;
    installLink?: string;
    installLinks?: { ru?: string; global?: string };
    subscriptionDescription: string;
    useDescription: string;
}

interface ImageInstructionData {
    platform: string;
    protocol: string;
    icon: string;
    images: {
        step: number;
        title: string;
        description: string;
        imageUrl: string;
        imageAlt: string;
    }[];
}

function InstructionDetailPage() {
    const {platform, protocol} = useParams<{ platform: string; protocol: string }>();
    const navigate = useNavigate();
    const [selectedProtocol, setSelectedProtocol] = useState<string>(protocol || 'vless');
    const [showProtocolModal, setShowProtocolModal] = useState<boolean>(!protocol);
    const [viewMode, setViewMode] = useState<'text' | 'image'>('image'); // По умолчанию показываем картинки
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }

        // ДОБАВИТЬ ЭТОТ ТАЙМЕР
        const timer = setTimeout(() => {
            setLoading(false);
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    const instruction = instructionsData[platform as string]?.[selectedProtocol];
    const imageInstruction = imageInstructionsData[platform as string]?.[selectedProtocol];
    const isVless = selectedProtocol === 'vless';
    const hasImageInstruction = imageInstruction;

    const handleProtocolSelect = (proto: string) => {
        setSelectedProtocol(proto);
        setShowProtocolModal(false);
        navigate(`/instructions/${platform}/${proto}`, {replace: true});
        // При смене протокола, если это не VLESS, переключаем на текстовый режим
        if (proto !== 'vless') {
            setViewMode('text');
        } else {
            setViewMode('image');
        }
    };

    const protocolIconsMap: Record<string, React.ReactNode> = {
        wireguard: <WireGuardIcon/>,
        amneziawg: <AmneziaWgIcon/>,
        vless: <VlessIcon/>,
        vless_incy: <IncyIcon/>,
    };

    if (!platform || !instructionsData[platform]) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="instruction-detail-page">
                    <div className="error-container">
                        <div className="error-icon">❌</div>
                        <h2>Платформа не найдена</h2>
                        <button className="continue-button" onClick={() => navigate('/install')}>
                            Вернуться к выбору платформы
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!instruction) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="instruction-detail-page">
                    <div className="error-container">
                        <div className="error-icon">❌</div>
                        <h2>Инструкция не найдена</h2>
                        <button className="continue-button" onClick={() => setShowProtocolModal(true)}>
                            Выбрать протокол
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="instruction-detail-page loading">
                    <div className="instruction-loader"><Loader /></div>
                </div>
            </div>
        );
    }

    const platformData = instructionsData[platform];

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar/>

            <div className="instruction-detail-page">
                <div className="page-header">
                    <button className="back-button" onClick={() => navigate('/install')}>
                        <BackIcon/>
                    </button>
                    <h1 className="page-title">Инструкция</h1>
                </div>

                <div className="instruction-header">
                    <div className="instruction-hero">
                        <div className="hero-icon2">
                            <img src={instruction.icon} alt={instruction.platform}/>
                        </div>
                    </div>
                    <div className="instruction-hero">
                        <div className="hero-info">
                            <button className="protocol-selector" onClick={() => setShowProtocolModal(true)}>
                            <span className="hero-badge">
                                <span className="protocol-icon-inst">
                                    {protocolIconsMap[selectedProtocol]}
                                </span>
                                <span className="protocol-text">
                                    {instruction.protocol}
                                </span>
                                                                <svg className="protocol-arrow" width="16" height="16"
                                                                     viewBox="0 0 24 24" fill="none"
                                                                     stroke="currentColor"
                                                                     strokeWidth="2">
                                    <path d="M6 9l6 6 6-6"/>
                                </svg>
                            </span>

                            </button>
                        </div>
                    </div>
                </div>

                {/* Переключатель между текстовой и визуальной инструкцией (только для VLESS) */}
                {hasImageInstruction && (
                    <div className="view-mode-switch">
                        <button
                            className={`view-mode-btn ${viewMode === 'image' ? 'active' : ''}`}
                            onClick={() => setViewMode('image')}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2">
                                <rect x="2" y="2" width="20" height="20" rx="2"/>
                                <circle cx="8.5" cy="8.5" r="2.5"/>
                                <path d="M21 15l-5-4-3 3-4-4-5 5"/>
                            </svg>
                            Инструкция с картинками
                        </button>
                        <button
                            className={`view-mode-btn ${viewMode === 'text' ? 'active' : ''}`}
                            onClick={() => setViewMode('text')}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2">
                                <path d="M4 7h16M4 12h16M4 17h10"/>
                            </svg>
                            Текстовая инструкция
                        </button>
                    </div>
                )}

                <div className="instruction-content">
                    {viewMode === 'image' && hasImageInstruction ? (
                        // Инструкция с картинками
                        <div className="image-instructions">
                            {/* Кнопка скачивания приложения */}
                            {instruction.installLinks && (
                                <div className="install-links image-install-links">
                                    <a href={instruction.installLinks.ru} target="_blank" rel="noopener noreferrer"
                                       className="download-link">
                                        App Store (RU)
                                    </a>
                                    <a href={instruction.installLinks.global} target="_blank" rel="noopener noreferrer"
                                       className="download-link">
                                        App Store (Global)
                                    </a>
                                </div>
                            )}
                            {instruction.installLink && (
                                <a
                                    href={instruction.installLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="download-link"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="7 10 12 15 17 10"/>
                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                    Скачать приложение
                                </a>
                            )}
                            {imageInstruction.images.map((img, index) => (
                                <div key={img.step} className="image-step">
                                    <div className="step-header">
                                        <span className="step-number">{img.step}</span>
                                        <h3 className="step-title">{img.title}</h3>
                                    </div>
                                    <p className="step-description">{img.description}</p>
                                    <div className="step-image">
                                        <img
                                            src={img.imageUrl}
                                            alt={img.imageAlt}
                                            onError={(e) => {
                                                // Если картинка не загрузилась, показываем заглушку
                                                e.currentTarget.style.display = 'none';
                                                const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                                if (placeholder) placeholder.style.display = 'flex';
                                            }}
                                        />
                                        <div className="image-placeholder" style={{display: 'none'}}>
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
                                                 stroke="currentColor" strokeWidth="1.5">
                                                <rect x="2" y="2" width="20" height="20" rx="2"/>
                                                <circle cx="8.5" cy="8.5" r="2.5"/>
                                                <path d="M21 15l-5-4-3 3-4-4-5 5"/>
                                            </svg>
                                            <span>Изображение скоро появится</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        // Текстовая инструкция
                        <>
                            {/* Установка приложения */}
                            <div className="instruction-section">
                                <h3 className="section-title">
                                    <span className="section-number">1</span>
                                    Установка приложения
                                </h3>
                                <div className="section-content">
                                    <p>{instruction.installDescription}</p>
                                    {instruction.installLinks && (
                                        <div className="install-links">
                                            <a href={instruction.installLinks.ru} target="_blank"
                                               rel="noopener noreferrer" className="download-link">
                                                App Store (RU)
                                            </a>
                                            <a href={instruction.installLinks.global} target="_blank"
                                               rel="noopener noreferrer" className="download-link">
                                                App Store (Global)
                                            </a>
                                        </div>
                                    )}
                                    {instruction.installLink && (
                                        <a
                                            href={instruction.installLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="download-link"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                 stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                                <polyline points="7 10 12 15 17 10"/>
                                                <line x1="12" y1="15" x2="12" y2="3"/>
                                            </svg>
                                            Скачать приложение
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Добавление подписки */}
                            <div className="instruction-section">
                                <h3 className="section-title">
                                    <span className="section-number">2</span>
                                    Добавление подписки
                                </h3>
                                <div className="section-content">
                                    <p>{instruction.subscriptionDescription}</p>
                                </div>
                            </div>

                            {/* Подключение и использование */}
                            <div className="instruction-section">
                                <h3 className="section-title">
                                    <span className="section-number">3</span>
                                    Подключение и использование
                                </h3>
                                <div className="section-content">
                                    <p>{instruction.useDescription}</p>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Модальное окно выбора протокола */}
            {showProtocolModal &&
                createPortal(
                    <div className="protocol-modal-overlay" onClick={() => setShowProtocolModal(false)}>
                        <div className="protocol-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="protocol-modal-header">
                                <h3>Выберите протокол</h3>
                                <button className="protocol-modal-close" onClick={() => setShowProtocolModal(false)}>✕
                                </button>
                            </div>
                            <div className="protocol-modal-body">
                                {Object.entries(platformData).map(([key, data]) => (
                                    <button
                                        key={key}
                                        className={`protocol-option ${selectedProtocol === key ? 'active' : ''}`}
                                        onClick={() => handleProtocolSelect(key)}
                                    >
                                        <div className="protocol-option-left">
                                        <span className="protocol-option-icon">
                                            {protocolIconsMap[key]}
                                        </span>
                                            <span className="protocol-option-name">{data.protocol}</span>
                                        </div>
                                        <span className="protocol-option-arrow">→</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}

export default InstructionDetailPage;